import React, { useState, useEffect, useMemo, useRef } from 'react';

import { Row, Col } from '@shared/components/bootstrap';
import { useNumericURLSearchParam } from '@shared/hooks/use_numeric_url_search_param';
import { useURLSearchParams } from '@shared/hooks/use_url_search_params';

import { ItemsGrid } from '@admin/components/items/items_grid';
import { Pagination } from '@admin/components/pagination';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { usePaginatedItemsExplorerQuery, ItemFiltersInput, type ItemIncidentsInput } from '@admin/schema';
import type { IItem } from '@admin/types/item';
import { normalizeItemToGraphQL } from '@admin/utils/item_normalization';

import { ItemsExplorerFilters } from './filters';

const DEFAULT_PAGE = 1;
const RESULTS_PER_PAGE = 48;

type Filters = {
  page?: number;
} & ItemFiltersInput;

const parseItemFilterInputAccountIds = (params: URLSearchParams): ItemFiltersInput['accountIds'] => {
  if (!params.has('accountIds')) return;
  return params.getAll('accountIds');
};

const saveItemFiltersInputAccountIds = (params: URLSearchParams, filters: ItemFiltersInput) => {
  params.delete('accountIds');
  if (filters.accountIds && filters.accountIds.length > 0) {
    for (const accountId of filters.accountIds) {
      params.append('accountIds', accountId);
    }
  }
};

const parseItemFilterInputCategoryFilterType = (params: URLSearchParams): ItemFiltersInput['categoryFilterType'] => {
  if (!params.has('categoryFilterType')) return;
  return params.get('categoryFilterType');
};

const saveItemFiltersInputCategoryFilterType = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.categoryFilterType !== undefined && filters.categoryFilterType !== null) {
    params.set('categoryFilterType', String(filters.categoryFilterType));
  } else {
    params.delete('categoryFilterType');
  }
};

const parseItemFilterInputCategoryIds = (params: URLSearchParams): ItemFiltersInput['categoryIds'] => {
  if (!params.has('categoryIds')) return;
  return params.getAll('categoryIds');
};

const saveItemFiltersInputCategoryIds = (params: URLSearchParams, filters: ItemFiltersInput) => {
  params.delete('categoryIds');
  if (filters.categoryIds && filters.categoryIds.length > 0) {
    for (const categoryId of filters.categoryIds) {
      params.append('categoryIds', categoryId);
    }
  }
};

const parseItemFilterInputDimensionFilterType = (params: URLSearchParams): ItemFiltersInput['dimensionFilterType'] => {
  if (!params.has('dimensionFilterType')) return;
  return params.get('dimensionFilterType');
};

const saveItemFiltersInputDimensionFilterType = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.dimensionFilterType !== undefined && filters.dimensionFilterType !== null) {
    params.set('dimensionFilterType', String(filters.dimensionFilterType));
  } else {
    params.delete('dimensionFilterType');
  }
};

const parseItemFilterInputDimensionMaxCuft = (params: URLSearchParams): ItemFiltersInput['dimensionMaxCuft'] => {
  if (!params.has('dimensionMaxCuft')) return;
  return Number(params.get('dimensionMaxCuft'));
};

const saveItemFiltersInputDimensionMaxCuft = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.dimensionMaxCuft !== undefined && filters.dimensionMaxCuft !== null) {
    params.set('dimensionMaxCuft', String(filters.dimensionMaxCuft));
  } else {
    params.delete('dimensionMaxCuft');
  }
};

const parseItemFilterInputDimensionMinCuft = (params: URLSearchParams): ItemFiltersInput['dimensionMinCuft'] => {
  if (!params.has('dimensionMinCuft')) return;
  return Number(params.get('dimensionMinCuft'));
};

const saveItemFiltersInputDimensionMinCuft = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.dimensionMinCuft !== undefined && filters.dimensionMinCuft !== null) {
    params.set('dimensionMinCuft', String(filters.dimensionMinCuft));
  } else {
    params.delete('dimensionMinCuft');
  }
};

const parseItemFilterInputHeightFilterType = (params: URLSearchParams): ItemFiltersInput['heightFilterType'] => {
  if (!params.has('heightFilterType')) return;
  return params.get('heightFilterType');
};

const saveItemFiltersInputHeightFilterType = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.heightFilterType !== undefined && filters.heightFilterType !== null) {
    params.set('heightFilterType', String(filters.heightFilterType));
  } else {
    params.delete('heightFilterType');
  }
};

const parseItemFilterInputIncidents = (params: URLSearchParams): ItemFiltersInput['incidents'] => {
  if (!params.has('incidents')) return;
  const incidents = params.getAll('incidents');
  const closed: string[] = [];
  const open: string[] = [];
  for (const incident of incidents) {
    const [kind, name] = incident.split(':');
    if (kind === 'closed') closed.push(name);
    if (kind === 'open') open.push(name);
  }

  return {
    closed: closed.length > 0 ? closed : undefined,
    open: open.length > 0 ? open : undefined,
  };
};

const saveItemFiltersInputIncidents = (params: URLSearchParams, filters: ItemFiltersInput) => {
  params.delete('incidents');

  if (filters.incidents?.closed) {
    for (const incident of filters.incidents.closed) {
      params.append('incidents', `closed:${incident}`);
    }
  }

  if (filters.incidents?.open) {
    for (const incident of filters.incidents.open) {
      params.append('incidents', `open:${incident}`);
    }
  }
};

const parseItemFilterInputIncludeUndefinedHeight = (
  params: URLSearchParams,
): ItemFiltersInput['includeUndefinedHeight'] => {
  if (!params.has('includeUndefinedHeight')) return;
  const value = params.get('includeUndefinedHeight');
  if (value === String(true)) return true;
  if (value === String(false)) return false;
  return undefined;
};

const saveItemFiltersInputIncludeUndefinedHeight = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.includeUndefinedHeight !== undefined && filters.includeUndefinedHeight !== null) {
    params.set('includeUndefinedHeight', String(filters.includeUndefinedHeight));
  } else {
    params.delete('includeUndefinedHeight');
  }
};

const parseItemFilterInputIncludeUndefinedLength = (
  params: URLSearchParams,
): ItemFiltersInput['includeUndefinedLength'] => {
  const value = params.get('includeUndefinedLength');
  if (value === String(true)) return true;
  if (value === String(false)) return false;
  return undefined;
};

const saveItemFiltersInputIncludeUndefinedLength = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.includeUndefinedLength !== undefined && filters.includeUndefinedLength !== null) {
    params.set('includeUndefinedLength', String(filters.includeUndefinedLength));
  } else {
    params.delete('includeUndefinedLength');
  }
};

const parseItemFilterInputIncludeUndefinedWidth = (
  params: URLSearchParams,
): ItemFiltersInput['includeUndefinedWidth'] => {
  if (!params.has('includeUndefinedWidth')) return;
  const value = params.get('includeUndefinedWidth');
  if (value === String(true)) return true;
  if (value === String(false)) return false;
  return undefined;
};

const saveItemFiltersInputIncludeUndefinedWidth = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.includeUndefinedWidth !== undefined && filters.includeUndefinedWidth !== null) {
    params.set('includeUndefinedWidth', String(filters.includeUndefinedWidth));
  } else {
    params.delete('includeUndefinedWidth');
  }
};

const parseItemFilterInputInGroup = (params: URLSearchParams): ItemFiltersInput['inGroup'] => {
  if (!params.has('inGroup')) return;
  return params.get('inGroup');
};

const saveItemFiltersInputInGroup = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.inGroup !== undefined && filters.inGroup !== null) {
    params.set('inGroup', String(filters.inGroup));
  } else {
    params.delete('inGroup');
  }
};

const parseItemFilterInputItemStates = (params: URLSearchParams): ItemFiltersInput['itemStates'] => {
  if (!params.has('itemStates')) return;
  return params.getAll('itemStates');
};

const saveItemFiltersInputItemStates = (params: URLSearchParams, filters: ItemFiltersInput) => {
  params.delete('itemStates');
  if (filters.itemStates && filters.itemStates.length > 0) {
    for (const itemState of filters.itemStates) {
      params.append('itemStates', itemState);
    }
  }
};

const parseItemFilterInputLengthFilterType = (params: URLSearchParams): ItemFiltersInput['lengthFilterType'] => {
  if (!params.has('lengthFilterType')) return;
  return params.get('lengthFilterType');
};

const saveItemFiltersInputLengthFilterType = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.lengthFilterType !== undefined && filters.lengthFilterType !== null) {
    params.set('lengthFilterType', String(filters.lengthFilterType));
  } else {
    params.delete('lengthFilterType');
  }
};

const parseItemFilterInputMaterialCategoryIds = (params: URLSearchParams): ItemFiltersInput['materialCategoryIds'] => {
  if (!params.has('materialCategoryIds')) return;
  return params.getAll('materialCategoryIds');
};

const saveItemFiltersInputMaterialCategoryIds = (params: URLSearchParams, filters: ItemFiltersInput) => {
  params.delete('materialCategoryIds');
  if (filters.materialCategoryIds && filters.materialCategoryIds.length > 0) {
    for (const materialCategoryId of filters.materialCategoryIds) {
      params.append('materialCategoryIds', materialCategoryId);
    }
  }
};

const parseItemFilterInputMaxHeight = (params: URLSearchParams): ItemFiltersInput['maxHeight'] => {
  if (!params.has('maxHeight')) return;
  return Number(params.get('maxHeight'));
};

const saveItemFiltersInputMaxHeight = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.maxHeight !== undefined && filters.maxHeight !== null) {
    params.set('maxHeight', String(filters.maxHeight));
  } else {
    params.delete('maxHeight');
  }
};

const parseItemFilterInputMaxLength = (params: URLSearchParams): ItemFiltersInput['maxLength'] => {
  if (!params.has('maxLength')) return;
  return Number(params.get('maxLength'));
};

const saveItemFiltersInputMaxLength = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.maxLength !== undefined && filters.maxLength !== null) {
    params.set('maxLength', String(filters.maxLength));
  } else {
    params.delete('maxLength');
  }
};

const parseItemFilterInputMaxWidth = (params: URLSearchParams): ItemFiltersInput['maxWidth'] => {
  if (!params.has('maxWidth')) return;
  return Number(params.get('maxWidth'));
};

const saveItemFiltersInputMaxWidth = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.maxWidth !== undefined && filters.maxWidth !== null) {
    params.set('maxWidth', String(filters.maxWidth));
  } else {
    params.delete('maxWidth');
  }
};

const parseItemFilterInputMinHeight = (params: URLSearchParams): ItemFiltersInput['minHeight'] => {
  if (!params.has('minHeight')) return;
  return Number(params.get('minHeight'));
};

const saveItemFiltersInputMinHeight = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.minHeight !== undefined && filters.minHeight !== null) {
    params.set('minHeight', String(filters.minHeight));
  } else {
    params.delete('minHeight');
  }
};

const parseItemFilterInputMinLength = (params: URLSearchParams): ItemFiltersInput['minLength'] => {
  if (!params.has('minLength')) return;
  return Number(params.get('minLength'));
};

const saveItemFiltersInputMinLength = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.minLength !== undefined && filters.minLength !== null) {
    params.set('minLength', String(filters.minLength));
  } else {
    params.delete('minLength');
  }
};

const parseItemFilterInputMinWidth = (params: URLSearchParams): ItemFiltersInput['minWidth'] => {
  if (!params.has('minWidth')) return;
  return Number(params.get('minWidth'));
};

const saveItemFiltersInputMinWidth = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.minWidth !== undefined && filters.minWidth !== null) {
    params.set('minWidth', String(filters.minWidth));
  } else {
    params.delete('minWidth');
  }
};

const parseItemFilterInputMoverIds = (params: URLSearchParams): ItemFiltersInput['moverIds'] => {
  if (!params.has('moverIds')) return;
  return params.getAll('moverIds');
};

const saveItemFiltersInputMoverIds = (params: URLSearchParams, filters: ItemFiltersInput) => {
  params.delete('moverIds');
  if (filters.moverIds && filters.moverIds.length > 0) {
    for (const moverId of filters.moverIds) {
      params.append('moverIds', moverId);
    }
  }
};

const parseItemFilterInputPalletTypeIds = (params: URLSearchParams): ItemFiltersInput['palletTypeIds'] => {
  if (!params.has('palletTypeIds')) return;
  return params.getAll('palletTypeIds');
};

const saveItemFiltersInputPalletTypeIds = (params: URLSearchParams, filters: ItemFiltersInput) => {
  params.delete('palletTypeIds');
  if (filters.palletTypeIds && filters.palletTypeIds.length > 0) {
    for (const palletTypeId of filters.palletTypeIds) {
      params.append('palletTypeIds', palletTypeId);
    }
  }
};

const parseItemFilterInputPartCategoryIds = (params: URLSearchParams): ItemFiltersInput['partCategoryIds'] => {
  if (!params.has('partCategoryIds')) return;
  return params.getAll('partCategoryIds');
};

const saveItemFiltersInputPartCategoryIds = (params: URLSearchParams, filters: ItemFiltersInput) => {
  params.delete('partCategoryIds');
  if (filters.partCategoryIds && filters.partCategoryIds.length > 0) {
    for (const partCategoryId of filters.partCategoryIds) {
      params.append('partCategoryIds', partCategoryId);
    }
  }
};

const parseItemFilterInputPickupDateFrom = (params: URLSearchParams): ItemFiltersInput['pickupDateFrom'] => {
  if (!params.has('pickupDateFrom')) return;
  return params.get('pickupDateFrom');
};

const saveItemFiltersInputPickupDateFrom = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.pickupDateFrom) {
    params.set('pickupDateFrom', filters.pickupDateFrom);
  } else {
    params.delete('pickupDateFrom');
  }
};

const parseItemFilterInputPickupDateTill = (params: URLSearchParams): ItemFiltersInput['pickupDateTill'] => {
  if (!params.has('pickupDateTill')) return;
  return params.get('pickupDateTill');
};

const saveItemFiltersInputPickupDateTill = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.pickupDateTill) {
    params.set('pickupDateTill', filters.pickupDateTill);
  } else {
    params.delete('pickupDateTill');
  }
};

const parseItemFilterInputSizingSelections = (params: URLSearchParams): ItemFiltersInput['sizingSelections'] => {
  if (!params.has('sizingSelections')) return;
  return params.getAll('sizingSelections');
};

const saveItemFiltersInputSizingSelections = (params: URLSearchParams, filters: ItemFiltersInput) => {
  params.delete('sizingSelections');
  if (filters.sizingSelections && filters.sizingSelections.length > 0) {
    for (const sizingSelection of filters.sizingSelections) {
      params.append('sizingSelections', sizingSelection);
    }
  }
};

const parseItemFilterInputWarehouseIds = (params: URLSearchParams): ItemFiltersInput['warehouseIds'] => {
  if (!params.has('warehouseIds')) return;
  return params.getAll('warehouseIds');
};

const saveItemFiltersInputWarehouseIds = (params: URLSearchParams, filters: ItemFiltersInput) => {
  params.delete('warehouseIds');
  if (filters.warehouseIds && filters.warehouseIds.length > 0) {
    for (const warehouseId of filters.warehouseIds) {
      params.append('warehouseIds', warehouseId);
    }
  }
};

const parseItemFilterInputWidthFilterType = (params: URLSearchParams): ItemFiltersInput['widthFilterType'] => {
  if (!params.has('widthFilterType')) return;
  return params.get('widthFilterType');
};

const saveItemFiltersInputWidthFilterType = (params: URLSearchParams, filters: ItemFiltersInput) => {
  if (filters.widthFilterType !== undefined && filters.widthFilterType !== null) {
    params.set('widthFilterType', String(filters.widthFilterType));
  } else {
    params.delete('widthFilterType');
  }
};

const useFilters = () => {
  const [params, setParams] = useURLSearchParams();

  const filters = useMemo((): ItemFiltersInput => {
    return {
      accountIds: parseItemFilterInputAccountIds(params),
      categoryFilterType: parseItemFilterInputCategoryFilterType(params),
      categoryIds: parseItemFilterInputCategoryIds(params),
      dimensionFilterType: parseItemFilterInputDimensionFilterType(params),
      dimensionMaxCuft: parseItemFilterInputDimensionMaxCuft(params),
      dimensionMinCuft: parseItemFilterInputDimensionMinCuft(params),
      heightFilterType: parseItemFilterInputHeightFilterType(params),
      incidents: parseItemFilterInputIncidents(params),
      includeUndefinedHeight: parseItemFilterInputIncludeUndefinedHeight(params),
      includeUndefinedLength: parseItemFilterInputIncludeUndefinedLength(params),
      includeUndefinedWidth: parseItemFilterInputIncludeUndefinedWidth(params),
      inGroup: parseItemFilterInputInGroup(params),
      itemStates: parseItemFilterInputItemStates(params),
      lengthFilterType: parseItemFilterInputLengthFilterType(params),
      materialCategoryIds: parseItemFilterInputMaterialCategoryIds(params),
      maxHeight: parseItemFilterInputMaxHeight(params),
      maxLength: parseItemFilterInputMaxLength(params),
      maxWidth: parseItemFilterInputMaxWidth(params),
      minHeight: parseItemFilterInputMinHeight(params),
      minLength: parseItemFilterInputMinLength(params),
      minWidth: parseItemFilterInputMinWidth(params),
      moverIds: parseItemFilterInputMoverIds(params),
      palletTypeIds: parseItemFilterInputPalletTypeIds(params),
      partCategoryIds: parseItemFilterInputPartCategoryIds(params),
      pickupDateFrom: parseItemFilterInputPickupDateFrom(params),
      pickupDateTill: parseItemFilterInputPickupDateTill(params),
      sizingSelections: parseItemFilterInputSizingSelections(params),
      warehouseIds: parseItemFilterInputWarehouseIds(params),
      widthFilterType: parseItemFilterInputWidthFilterType(params),
    };
  }, [params]);

  const onFilter = (filters: ItemFiltersInput) => {
    setParams((params) => {
      params.delete('page');

      saveItemFiltersInputAccountIds(params, filters);
      saveItemFiltersInputCategoryFilterType(params, filters);
      saveItemFiltersInputCategoryIds(params, filters);
      saveItemFiltersInputDimensionFilterType(params, filters);
      saveItemFiltersInputDimensionMaxCuft(params, filters);
      saveItemFiltersInputDimensionMinCuft(params, filters);
      saveItemFiltersInputHeightFilterType(params, filters);
      saveItemFiltersInputIncidents(params, filters);
      saveItemFiltersInputIncludeUndefinedHeight(params, filters);
      saveItemFiltersInputIncludeUndefinedLength(params, filters);
      saveItemFiltersInputIncludeUndefinedWidth(params, filters);
      saveItemFiltersInputInGroup(params, filters);
      saveItemFiltersInputItemStates(params, filters);
      saveItemFiltersInputLengthFilterType(params, filters);
      saveItemFiltersInputMaterialCategoryIds(params, filters);
      saveItemFiltersInputMaxHeight(params, filters);
      saveItemFiltersInputMaxLength(params, filters);
      saveItemFiltersInputMaxWidth(params, filters);
      saveItemFiltersInputMinHeight(params, filters);
      saveItemFiltersInputMinLength(params, filters);
      saveItemFiltersInputMinWidth(params, filters);
      saveItemFiltersInputMoverIds(params, filters);
      saveItemFiltersInputPalletTypeIds(params, filters);
      saveItemFiltersInputPartCategoryIds(params, filters);
      saveItemFiltersInputPickupDateFrom(params, filters);
      saveItemFiltersInputPickupDateTill(params, filters);
      saveItemFiltersInputSizingSelections(params, filters);
      saveItemFiltersInputWarehouseIds(params, filters);
      saveItemFiltersInputWidthFilterType(params, filters);

      return params;
    });
  };

  return [filters, onFilter] as const;
};

export const ItemsExplorerContent: React.FC = () => {
  useTitle('Items Explorer');
  const [page, setPage] = useNumericURLSearchParam('page', DEFAULT_PAGE);
  const [filters, onFilter] = useFilters();

  const { data, loading, error } = usePaginatedItemsExplorerQuery({
    client,
    variables: {
      page,
      per: RESULTS_PER_PAGE,
      filters: Object.keys(filters).length > 0 ? filters : undefined,
    },
  });

  const items: IItem[] = useMemo(() => {
    const rawItems = data?.paginatedItemsExplorer?.results || [];
    // Normalize GraphQL items to IItem format (ensures both Angular and GraphQL field formats)
    return rawItems.map((item: any) => {
      const normalized = normalizeItemToGraphQL(item);
      // Ensure Angular format fields are also present for backward compatibility
      return {
        ...normalized,
        account_id: normalized.accountID,
        pallet_id: normalized.palletId,
        warehouse_id: normalized.warehouseId,
        group_id: normalized.groupId,
        latest_open_incident_kind: normalized.latestOpenIncidentKind,
        custom_category_name: normalized.customCategoryName,
        sizing_selection: normalized.sizingSelection,
        dimensions_overwritten: normalized.dimensionsOverwritten,
        num_movers: normalized.numMovers,
        ...(normalized.packaging && {
          packaging: {
            ...normalized.packaging,
            material_category_id: normalized.packaging.materialCategoryId,
            part_category_id: normalized.packaging.partCategoryId,
          },
        }),
      } as IItem;
    });
  }, [data?.paginatedItemsExplorer?.results]);
  const pagination = data?.paginatedItemsExplorer?.pagination;
  const hasFilters = Object.keys(filters).length > 0;

  return (
    <Row>
      <Col md={3}>
        <ItemsExplorerFilters filters={filters} onChange={onFilter} />
      </Col>
      <Col md={9}>
        {loading && <Spinner />}
        {error && <div className="alert alert-danger">Error loading items. Please try again.</div>}
        {!hasFilters && !loading && (
          <div className="text-center">
            <p>Use the filters on the left to search for items</p>
          </div>
        )}
        {hasFilters && !loading && items.length === 0 && (
          <div className="text-center">
            <div className="panel items-editor-filter-panel">
              <div className="panel-body">
                <span className="title">0 Items Found</span>
              </div>
            </div>
          </div>
        )}
        {hasFilters && !loading && items.length > 0 && (
          <>
            <div className="panel items-editor-filter-panel">
              <div className="panel-body">
                <span className="title">
                  {pagination?.totalCount || 0} {pagination?.totalCount === 1 ? 'Item' : 'Items'} Found
                </span>
              </div>
            </div>
            <ItemsGrid items={items} isSizeable={true} className="col-md-6 col-lg-4" />
            {pagination && <Pagination pagination={pagination} onPage={setPage} />}
          </>
        )}
      </Col>
    </Row>
  );
};
